import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import {
  ConversionPayload,
  ExtractionPayload,
  RolePayload,
  SubtypePayload,
} from "../../DocumentAnalysisAPI";

/**
 * The answer, in one line.
 *
 * The stepper above shows how the run got here; this says where it arrived. Worth its own place
 * because the steps are read while they run, and by the time all three are done the eye is looking
 * for the conclusion rather than the trail.
 */
export default function ResultCard({
  role,
  subtype,
  conversion,
  extraction,
}: {
  role?: RolePayload;
  subtype?: SubtypePayload;
  conversion?: ConversionPayload;
  extraction?: ExtractionPayload;
}) {
  const { t } = useTranslation();

  const percent = (confidence?: number) => `${Math.round((confidence ?? 0) * 100)} %`;

  return (
    <div className="rounded-lg border bg-background p-4">
      <h4 className="mb-3 text-sm font-semibold">{t("documentAnalysisPanel.test.result")}</h4>

      {role?.role ? (
        <div className="flex flex-wrap items-center gap-2 text-base">
          <span className="font-semibold">{role.role}</span>
          <span className="text-xs text-muted-foreground">{percent(role.confidence)}</span>

          {subtype?.subtype ? (
            <>
              <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="font-semibold">{subtype.subtype}</span>
              {subtype.parameterValue ? <span>= {subtype.parameterValue}</span> : null}
              <span className="text-xs text-muted-foreground">{percent(subtype.confidence)}</span>
            </>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("documentAnalysisPanel.test.undecided")}
          {role?.undecidedReason ? <> — {role.undecidedReason}</> : null}
        </p>
      )}

      {extraction?.report ? (
        <p className="mt-2 text-sm">
          {t("documentAnalysisPanel.test.instanceCount", { count: extraction.report.instanceCount })}
          {extraction.report.movedToUnmapped + extraction.report.typeViolations > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              {" "}
              ·{" "}
              {t("documentAnalysisPanel.test.movedToUnmapped", {
                count: extraction.report.movedToUnmapped + extraction.report.typeViolations,
              })}
            </span>
          ) : null}
        </p>
      ) : null}

      {/*
        * Was am Katalog zu tun ist, nicht am Dokument.
        *
        * Beides sind Befunde über den Rollenkatalog, und beide wurden bisher berechnet und nirgends
        * gezeigt: eine Klasse, die es im Modell nicht mehr gibt, und ein Begriff, für den es
        * inzwischen ein Feld gibt. Der zweite ist der teurere — solange er im Katalog steht, darf
        * die Extraktion die Sache nicht in ihr Feld legen, und das freie Objekt taucht danach als
        * fehlende Modellklasse auf. Der Lauf hat ihn umgeleitet; der Eintrag gehört trotzdem weg.
        */}
      {extraction?.report?.coveredTerms?.length || extraction?.report?.unknownClasses?.length ? (
        <div className="mt-2 space-y-1 text-xs text-amber-700 dark:text-amber-300">
          {extraction.report.coveredTerms?.length ? (
            <p>
              {t("documentAnalysisPanel.test.coveredTerms")}{" "}
              {extraction.report.coveredTerms.join(", ")}
            </p>
          ) : null}
          {extraction.report.unknownClasses?.length ? (
            <p>
              {t("documentAnalysisPanel.test.unknownClasses")}{" "}
              {extraction.report.unknownClasses.join(", ")}
            </p>
          ) : null}
        </div>
      ) : null}

      {conversion ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t(`documentAnalysisPanel.test.format.${conversion.format}`)} ·{" "}
          {t("documentAnalysisPanel.test.chars", { count: conversion.meta?.chars ?? 0 })}
        </p>
      ) : null}
    </div>
  );
}
